// import type { Breadcrumb } from "@sentry/types";
import type { ErrorEvent, EventHint } from "@sentry/vue";
import { enableSentryTracing, Sentry as HttpHelpersSentry } from "@toruslabs/http-helpers";
import log from "loglevel";
import { App } from "vue";
import { Router } from "vue-router";

import appConfig from "../config";
import { isUnauthorizedError } from "./utils";

// `@toruslabs/http-helpers` (get/post/patch/put/remove) rejects with the raw `fetch` `Response`
// object on non-OK responses. When such a rejection is not handled it surfaces as an
// `onunhandledrejection`, and Sentry stringifies the value to the unactionable
// `Error: [object Response]` (no stacktrace). Normalize these here so expected auth failures are
// dropped and the rest are reported as meaningful, well-grouped HTTP errors.
function normalizeResponseError(event: ErrorEvent, hint?: EventHint): ErrorEvent | null {
  const originalException = hint?.originalException;
  if (!(originalException instanceof Response)) return event;

  // Expected auth failures (e.g. expired/invalid session) are not actionable.
  if (originalException.status === 401 || originalException.status === 403) return null;

  const { status, url } = originalException;
  let path = url;
  try {
    path = new URL(url).pathname;
  } catch {
    // keep the full url if it cannot be parsed
  }

  // Group by status + endpoint instead of the useless "[object Response]".
  event.fingerprint = ["http-response-error", String(status), path];
  const value = event.exception?.values?.[0];
  if (value) {
    value.type = "HttpResponseError";
    value.value = `Unhandled HTTP ${status} response from ${url || path}`;
  }

  return event;
}

export async function installSentry(app: App, router: Router): Promise<void> {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  const [Sentry, loglevelSentryModule] = await Promise.all([import("@sentry/vue"), import("@toruslabs/loglevel-sentry")]);

  const { LoglevelSentry } = loglevelSentryModule;

  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: appConfig.buildEnv,
    release: `wallet-services@${appConfig.appVersion}`,
    integrations: [
      Sentry.browserTracingIntegration({
        router,
      }),
    ],
    tracePropagationTargets: [appConfig.api, appConfig.commonApiHost],
    ignoreErrors: [
      // Happen when user click 'X' on the browser (ref https://forum.sentry.io/t/typeerror-failed-to-fetch-reported-over-and-overe/8447/2)
      "TypeError: Failed to fetch", // All except iOS and Firefox
      "TypeError: cancelled", // iOS
      "TypeError: NetworkError when attempting to fetch resource.", // Firefox
      // Noisy handled errors from background block polling (RPC returns non-JSON, e.g. "project ID ..."). No user impact.
      "PollingBlockTracker",
      "Polling Block Tracker:",
      // Transient socket.io/engine.io transport errors from @toruslabs/broadcast-channel &
      // @toruslabs/secure-pub-sub. Non-actionable: socket auto-reconnects.
      "xhr poll error",
      "xhr post error",
      "websocket error",
      "transport close",
      "transport error",
    ],
    sampleRate: Number.parseFloat(appConfig.sentrySampleRate),
    tracesSampleRate: Number.parseFloat(appConfig.sentryTransactionSampleRate),
    normalizeDepth: 8,
    // beforeBreadcrumb(breadcrumb: Breadcrumb) {
    // breadcrumb.data = redactBreadcrumbData(breadcrumb.data);
    // return breadcrumb;
    // },
    beforeSend: normalizeResponseError,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
  });

  const plugin = new LoglevelSentry(Sentry);
  plugin.install(log);

  // https://github.com/pimterry/loglevel?tab=readme-ov-file#writing-plugins
  const originalFactory = log.methodFactory;
  log.methodFactory = (method, level, name) => {
    const rawMethod = originalFactory(method, level, name);

    if (method === "error") {
      return async (...args: unknown[]) => {
        // don't log unauthorised errors
        if (args.find(isUnauthorizedError)) return undefined;
        return rawMethod(...args);
      };
    }

    return (...args: unknown[]) => rawMethod(...args);
  };
  log.rebuild();

  enableSentryTracing(Sentry as unknown as HttpHelpersSentry, [appConfig.api, appConfig.commonApiHost], []);
}
